import React, { useState } from 'react';
import Input from './Input';
import { Search, Mail, Lock, User, Calendar, Clock, MapPin, Users } from 'lucide-react';

const InputShowcase: React.FC = () => {
  const [values, setValues] = useState({
    email: '',
    password: '',
    search: '',
    username: '',
    date: '',
    time: '',
    location: '',
    guests: ''
  });

  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });

  const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues(prev => ({ ...prev, [field]: e.target.value }));
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleValidation = () => {
    const newErrors = { email: '', password: '' };
    
    if (!values.email.includes('@')) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (values.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    setErrors(newErrors);
  };

  return (
    <div className="max-w-4xl mx-auto p-8 space-y-12 bg-gray-50 min-h-screen">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Enhanced Input Components</h1>
        <p className="text-gray-600">Showcasing the new Mirsal-themed input designs</p>
      </div>

      {/* Mirsal Variant (Default) */}
      <section className="bg-white rounded-xl p-8 shadow-lg">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6 flex items-center">
          <div className="w-3 h-3 bg-mirsal-green rounded-full mr-3"></div>
          Mirsal Theme (Default)
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Email Address"
            type="email"
            placeholder="Enter your email"
            leftIcon={<Mail />}
            value={values.email}
            onChange={handleChange('email')}
            error={errors.email}
            helpText="We'll never share your email with anyone"
            required
          />
          
          <Input
            label="Password"
            type="password"
            placeholder="Enter your password"
            leftIcon={<Lock />}
            value={values.password}
            onChange={handleChange('password')}
            error={errors.password}
            helpText="Minimum 6 characters"
            required
          />
          
          <Input
            label="Search Events"
            placeholder="Search for events..."
            leftIcon={<Search />}
            value={values.search}
            onChange={handleChange('search')}
            helpText="🔍 Try typing to see the focus effects"
          />
          
          <Input
            label="Username"
            placeholder="Choose a username"
            leftIcon={<User />}
            value={values.username}
            onChange={handleChange('username')}
            helpText="This will be your display name"
          />
        </div>
        
        <div className="mt-6">
          <button
            onClick={handleValidation}
            className="px-6 py-2 bg-mirsal-green text-black font-medium rounded-lg hover:bg-mirsal-green-dark transition-colors duration-200"
          >
            Test Validation
          </button>
        </div>
      </section>

      {/* Different Sizes */}
      <section className="bg-white rounded-xl p-8 shadow-lg">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">Size Variants</h2>
        <div className="space-y-6">
          <Input
            label="Small Input"
            inputSize="sm"
            placeholder="Small size input"
            leftIcon={<User />}
            helpText="Size: sm"
          />
          
          <Input
            label="Medium Input (Default)"
            inputSize="md"
            placeholder="Medium size input"
            leftIcon={<Mail />}
            helpText="Size: md"
          />
          
          <Input
            label="Large Input"
            inputSize="lg"
            placeholder="Large size input"
            leftIcon={<Search />}
            helpText="Size: lg"
          />
        </div>
      </section>

      {/* Event Form Styled Inputs */}
      <section className="bg-white rounded-xl p-8 shadow-lg">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">Event Form Styling</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Event Date"
            type="date"
            leftIcon={<Calendar />}
            value={values.date}
            onChange={handleChange('date')}
            helpText="Select your event date"
            required
          />
          
          <Input
            label="Event Time"
            type="time"
            leftIcon={<Clock />}
            value={values.time}
            onChange={handleChange('time')}
            helpText="What time will it start?"
            required
          />
          
          <Input
            label="Location"
            placeholder="e.g., Grand Ballroom, Central Park"
            leftIcon={<MapPin />}
            value={values.location}
            onChange={handleChange('location')}
            helpText="Where will your event take place?"
          />
          
          <Input
            label="Guest Limit (Optional)"
            type="number"
            placeholder="Enter max guests or leave empty for unlimited"
            leftIcon={<Users />}
            value={values.guests}
            onChange={handleChange('guests')}
            helpText="💡 Leave empty to allow unlimited guests"
          />
        </div>
      </section>

      {/* Default and Elegant Variants */}
      <section className="bg-white rounded-xl p-8 shadow-lg">
        <h2 className="text-2xl font-semibold text-gray-800 mb-6">Alternative Variants</h2>
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium text-gray-700 mb-3">Default Variant</h3>
            <Input
              variant="default"
              label="Default Style Input"
              placeholder="Traditional input styling"
              leftIcon={<Mail />}
              helpText="Classic blue focus ring"
            />
          </div>
          
          <div>
            <h3 className="text-lg font-medium text-gray-700 mb-3">Elegant Variant</h3>
            <Input
              variant="elegant"
              label="Elegant Underline Style"
              placeholder="Minimalist underline design"
              leftIcon={<User />}
              helpText="Underline focus effect"
            />
          </div>
        </div>
      </section>

      {/* Interactive Demo */}
      <section className="bg-gradient-to-r from-mirsal-green to-green-400 rounded-xl p-8 text-black">
        <h2 className="text-2xl font-semibold mb-4">✨ Interactive Features</h2>
        <div className="bg-white bg-opacity-90 rounded-lg p-6">
          <ul className="space-y-2 text-sm">
            <li className="flex items-center">
              <span className="w-2 h-2 bg-mirsal-green rounded-full mr-3"></span>
              Smooth focus animations with scale effect
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-mirsal-green rounded-full mr-3"></span>
              Dynamic label and icon color changes
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-mirsal-green rounded-full mr-3"></span>
              Animated focus indicator bar
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-mirsal-green rounded-full mr-3"></span>
              Enhanced shadow effects on hover/focus
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-mirsal-green rounded-full mr-3"></span>
              Animated error messages with icons
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-mirsal-green rounded-full mr-3"></span>
              Responsive size variants
            </li>
          </ul>
        </div>
      </section>
    </div>
  );
};

export default InputShowcase;
